/**
 * Invite — validates a waitlist invite token, shows a welcome screen, then
 * sends the invited user to account creation before product access.
 *
 * Flow:
 *   1. Validate token → loading spinner
 *   2. Valid   → show welcome screen → click CTA → acceptInvite → navigate
 *               to Clerk signup. The product root only renders after sign-in.
 *   3. Invalid → show error with link to waitlist
 *
 * NOTE (PRI-442, 2026-07-09): this page must NOT render the marketing carousel.
 * Invite links now force account creation; anonymous product trials are closed.
 */

import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { validateInviteToken, acceptInvite } from '../lib/api'
import { trackInviteAccepted } from '../lib/analytics'
import { useIsMobile } from '../hooks/useIsMobile'

// ── Brand palette — Light Mode (brand-guidelines-v3.2) ───────────────────────
const L = {
  bgPage:      '#F2F0EB',
  bgSurface:   '#FAFAF7',
  bgCanvas:    '#E8E5DE',
  border:      '#D4D0C8',
  textPrimary: '#1A1916',
  textMuted:   '#7A7669',
  textLight:   '#A8A39A',
  signal:      '#1B4FE0',
  signalHover: '#1340C8',
}

type Status = 'loading' | 'ready' | 'accepting' | 'invalid'

// ── LogoMark ─────────────────────────────────────────────────────────────────
function LogoMark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <img
        src="/Darwin_v3_vinyl_transparent.png"
        alt=""
        style={{ height: 36, width: 'auto', objectFit: 'contain', marginRight: 10 }}
      />
      <div style={{ display: 'flex', lineHeight: 1.15 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: L.textPrimary, letterSpacing: '-0.03em' }}>
          Primate
        </span>
        <span style={{ fontSize: 20, fontWeight: 700, color: L.signal, letterSpacing: '-0.03em' }}>
          Intelligence
        </span>
      </div>
    </div>
  )
}

// ── PageFooter ────────────────────────────────────────────────────────────────
function PageFooter() {
  return (
    <div style={{
      marginTop: 'auto',
      paddingTop: 32,
      paddingBottom: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 20,
      borderTop: `1px solid ${L.border}`,
    }}>
      <span style={{ fontSize: 12, color: L.textLight }}>
        © {new Date().getFullYear()} Primate AI, Inc.
      </span>
      <a href="/privacy" style={{ fontSize: 12, color: L.textLight, textDecoration: 'none' }}>Privacy</a>
      <a href="/terms"   style={{ fontSize: 12, color: L.textLight, textDecoration: 'none' }}>Terms</a>
    </div>
  )
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <>
      <style>{`@keyframes inv-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        width: 28, height: 28,
        border: `2.5px solid ${L.border}`,
        borderTopColor: L.signal,
        borderRadius: '50%',
        animation: 'inv-spin 0.7s linear infinite',
      }} />
    </>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
const pageStyles = `
  @keyframes inv-fade-up {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .inv-item-1 { animation: inv-fade-up 0.55s cubic-bezier(0.16,1,0.3,1) 0.00s both; }
  .inv-item-2 { animation: inv-fade-up 0.55s cubic-bezier(0.16,1,0.3,1) 0.10s both; }


  .inv-cta:hover  { background: ${L.signalHover} !important; }
  .inv-cta:active { transform: scale(0.985); }

  @media (max-width: 640px) {
    .inv-header  { padding-left: 16px !important; padding-right: 16px !important; }
    .inv-content { padding: 24px 16px 0 !important; }
    .inv-card    { padding: 24px 20px 20px !important; }

  }
`

// ── Main component ────────────────────────────────────────────────────────────
export default function Invite() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const token = searchParams.get('token') ?? ''

  const [status, setStatus] = useState<Status>('loading')
  const [firstName, setFirstName] = useState('')

  useEffect(() => {
    if (!token) { setStatus('invalid'); return }
    validateInviteToken(token)
      .then(data => {
        if (data.valid) {
          setFirstName(data.full_name ? data.full_name.split(' ')[0] : '')
          setStatus('ready')
        } else {
          setStatus('invalid')
        }
      })
      .catch(() => setStatus('invalid'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleGetStarted() {
    setStatus('accepting')
    try {
      await acceptInvite(token)
    } catch {
      // already accepted is fine — proceed
    }
    localStorage.removeItem('primate-invited')
    trackInviteAccepted()
    navigate(`/signup?invite=${encodeURIComponent(token)}`, { replace: true })
  }

  const pageShell: React.CSSProperties = {
    minHeight: '100vh',
    background: L.bgPage,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
    boxSizing: 'border-box',
  }

  const header: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: isMobile ? '16px 16px' : '20px 40px',
    borderBottom: `1px solid ${L.border}`,
    background: L.bgPage,
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div style={{ ...pageShell, alignItems: 'center', justifyContent: 'center' }}>
        <img src="/Darwin_v3_vinyl_transparent.png" alt="Primate Intelligence" style={{ height: 36, marginBottom: 20, objectFit: 'contain' }} />
        <Spinner />
        <p style={{ marginTop: 14, fontSize: 14, color: L.textMuted }}>Verifying your invite…</p>
      </div>
    )
  }

  // ── Invalid ──────────────────────────────────────────────────────────────
  if (status === 'invalid') {
    return (
      <div style={{ ...pageShell, alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <img src="/Darwin_v3_vinyl_transparent.png" alt="Primate Intelligence" style={{ height: 36, marginBottom: 20, objectFit: 'contain' }} />
        <div style={{
          background: L.bgSurface, border: `1px solid ${L.border}`, borderRadius: 16,
          padding: '28px 28px 24px', maxWidth: 380, width: '100%', textAlign: 'center',
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: L.textPrimary, marginBottom: 8, letterSpacing: '-0.02em' }}>
            Invite link unavailable
          </h2>
          <p style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.6, marginBottom: 20 }}>
            This invite link has already been used or is no longer valid.
          </p>
          <a href="/waitlist" style={{
            display: 'inline-block',
            background: L.signal, color: '#FFFFFF',
            fontWeight: 600, fontSize: 14,
            padding: '10px 22px', borderRadius: 8,
            textDecoration: 'none',
          }}>
            Join the waitlist
          </a>
        </div>
      </div>
    )
  }

  // ── Welcome screen ───────────────────────────────────────────────────────
  return (
    <>
      <style>{pageStyles}</style>
      <div style={pageShell}>

        {/* Header */}
        <div className="inv-header" style={header}>
          <LogoMark />
        </div>

        {/* Content */}
        <div className="inv-content inv-item-1" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: isMobile ? '28px 16px 0' : '48px 24px 0',
          width: '100%',
          maxWidth: 1040,
          margin: '0 auto',
          boxSizing: 'border-box',
          flex: 1,
        }}>

          {/* Welcome card */}
          <div className="inv-card inv-item-2" style={{
            width: '100%',
            maxWidth: 560,
            background: L.bgSurface,
            border: `1px solid ${L.border}`,
            borderRadius: 20,
            padding: isMobile ? '24px 20px 20px' : '32px 32px 28px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04),0 4px 16px rgba(0,0,0,0.07),0 16px 48px rgba(0,0,0,0.05)',
            marginBottom: 36,
            textAlign: 'center',
          }}>

            {/* Eyebrow */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '3px 10px',
              borderRadius: 8,
              background: 'rgba(27,79,224,0.08)',
              border: '1px solid rgba(27,79,224,0.2)',
              marginBottom: 14,
            }}>
              <span style={{
                fontSize: 9,
                fontWeight: 700,
                fontFamily: '"JetBrains Mono", monospace',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: L.signal,
              }}>
                Early Access
              </span>
            </div>

            <h1 style={{
              fontSize: isMobile ? 24 : 28,
              fontWeight: 800,
              color: L.textPrimary,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              marginBottom: 10,
            }}>
              {firstName ? `Welcome, ${firstName}.` : 'Welcome.'}
            </h1>
            <p style={{
              fontSize: 15,
              color: L.textMuted,
              lineHeight: 1.65,
              maxWidth: 400,
              margin: '0 auto 24px',
            }}>
              You've been invited to Primate Intelligence early access.
              Create your account to start streaming from your camera or upload a video.
            </p>

            {/* Divider */}
            <div style={{ height: 1, background: L.border, marginBottom: 22 }} />

            {/* What's inside */}
            <p style={{
              fontSize: 10,
              fontFamily: '"JetBrains Mono", monospace',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: L.textMuted,
              marginBottom: 14,
              textAlign: 'left',
            }}>
              What you can do
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, textAlign: 'left' }}>
              {[
                { n: '01', t: 'Stream live from your webcam and ask Darwin what to look for.' },
                { n: '02', t: 'Upload any video and get deterministic, real-time scene understanding.' },
                { n: '03', t: 'Use the API — credentials are in your account once you sign up.' },
              ].map(({ n, t }) => (
                <div key={n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 10, fontWeight: 700,
                    color: L.signal, letterSpacing: '0.08em',
                    flexShrink: 0, marginTop: 2, opacity: 0.85,
                  }}>{n}</span>
                  <span style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.55 }}>{t}</span>
                </div>
              ))}
            </div>

            {/* Matt section */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              textAlign: 'left',
              padding: '12px 14px',
              borderRadius: 10,
              background: L.bgCanvas,
              border: `1px solid ${L.border}`,
              marginBottom: 24,
            }}>
              <img src="/images/headshot-matt.jpg" alt="Matt Miesnieks"
                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: 13, color: L.textPrimary, lineHeight: 1.55, margin: 0 }}>
                  <strong style={{ fontWeight: 600 }}>Matt Miesnieks</strong>, CEO —
                  reach out any time with feedback.
                </p>
                <p style={{ fontSize: 12, color: L.textMuted, margin: '2px 0 0' }}>
                  matt@primateintelligence.ai
                </p>
              </div>
            </div>

            {/* CTA */}
            <button
              className="inv-cta"
              onClick={handleGetStarted}
              disabled={status === 'accepting'}
              style={{
                width: '100%',
                borderRadius: 10,
                background: L.signal,
                border: 'none',
                padding: '13px 24px',
                fontSize: 15,
                fontWeight: 600,
                color: '#FFFFFF',
                cursor: status === 'accepting' ? 'wait' : 'pointer',
                letterSpacing: '-0.01em',
                transition: 'background 150ms, transform 80ms',
                fontFamily: 'Inter, -apple-system, sans-serif',
                opacity: status === 'accepting' ? 0.7 : 1,
              }}
            >
              {status === 'accepting' ? 'Setting up…' : 'Create account →'}
            </button>
          </div>

        </div>

        <div style={{ padding: '0 24px' }}>
          <PageFooter />
        </div>

      </div>
    </>
  )
}
